<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { getItemValueSummary } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import MdiTrendingDown from '~icons/mdi/trending-down'
import MdiAlertCircleOutline from '~icons/mdi/alert-circle-outline'
import MdiSortAscending from '~icons/mdi/sort-ascending'
import MdiSortDescending from '~icons/mdi/sort-descending'

// ─── State ────────────────────────────────────────────────────────────────────

const { t } = useI18n()

const loading   = ref(false)
const loadError = ref(null)
const data      = ref(null)

const groupBy  = ref('category')
const sortKey  = ref('total_purchase_value')
const sortDir  = ref('desc')

const GROUP_OPTIONS = computed(() => [
  { value: 'category',  label: t('assetValues.groupOptions.category') },
  { value: 'location',  label: t('assetValues.groupOptions.location') },
  { value: 'warehouse', label: t('assetValues.groupOptions.warehouse') },
  { value: 'supplier',  label: t('assetValues.groupOptions.supplier') },
])

// ─── Data loading ─────────────────────────────────────────────────────────────

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    data.value = await getItemValueSummary({ group_by: groupBy.value })
  } catch (e) {
    loadError.value = e?.data?.detail ?? t('assetValues.loadFailed')
  } finally {
    loading.value = false
  }
}

watch(groupBy, load)
onMounted(load)

// ─── Computed ─────────────────────────────────────────────────────────────────

const totals = computed(() => data.value?.totals ?? null)
const missingCount = computed(() => {
  if (!totals.value) return 0
  return (totals.value.asset_count ?? 0) - (totals.value.valued_count ?? 0)
})

const depreciationPct = computed(() => {
  const t = totals.value
  if (!t || parseFloat(t.total_purchase_value) === 0) return null
  return ((parseFloat(t.total_depreciation) / parseFloat(t.total_purchase_value)) * 100).toFixed(1)
})

const sortedGroups = computed(() => {
  const groups = [...(data.value?.groups ?? [])]
  groups.sort((a, b) => {
    const av = parseFloat(a[sortKey.value]) || a[sortKey.value] || 0
    const bv = parseFloat(b[sortKey.value]) || b[sortKey.value] || 0
    if (typeof av === 'string') return sortDir.value === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
    return sortDir.value === 'asc' ? av - bv : bv - av
  })
  return groups
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(parseFloat(val))
}

function fmtPct(pv, bv) {
  const p = parseFloat(pv)
  if (!p) return '—'
  return (((p - parseFloat(bv)) / p) * 100).toFixed(1) + '%'
}

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'desc'
  }
}
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="$t('assetValues.title')" :show-back="false">
      <template #actions>
        <button class="btn ghost small" :disabled="loading" @click="load">
          {{ loading ? $t('common.loading') : $t('common.refresh') }}
        </button>
      </template>
    </BasePageHeader>

    <div class="px-4 pt-4 flex flex-col gap-5 pb-10">

      <!-- Error -->
      <div v-if="loadError" class="flex items-center gap-2 text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-xl px-4 py-3">
        <MdiAlertCircleOutline class="w-4 h-4 shrink-0" />
        {{ loadError }}
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading && !data" class="flex flex-col gap-3 animate-pulse">
        <div class="h-24 bg-gray-800 rounded-2xl" />
        <div class="h-10 bg-gray-800 rounded-xl" />
        <div class="h-64 bg-gray-800 rounded-2xl" />
      </div>

      <template v-if="data">

        <!-- ── Summary banner ─────────────────────────────────────── -->
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div class="data-card flex flex-col gap-1">
            <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('assetValues.totalAssets') }}</span>
            <span class="text-xl font-bold text-gray-100">{{ totals?.asset_count ?? '—' }}</span>
            <span v-if="missingCount > 0" class="text-[0.65rem] text-amber-400">
              {{ $t('assetValues.withoutValuation', { n: missingCount }) }}
            </span>
          </div>
          <div class="data-card flex flex-col gap-1">
            <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('assetValues.purchaseValue') }}</span>
            <span class="text-lg font-bold text-gray-100">{{ fmtCurrency(totals?.total_purchase_value) }}</span>
            <span class="text-[0.65rem] text-[var(--color-muted)]">{{ $t('assetValues.valuedAssets', { n: totals?.valued_count ?? 0 }) }}</span>
          </div>
          <div class="data-card flex flex-col gap-1">
            <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('assetValues.bookValue') }}</span>
            <span class="text-lg font-bold text-amber-300">{{ fmtCurrency(totals?.total_book_value) }}</span>
            <span class="text-[0.65rem] text-[var(--color-muted)]">{{ $t('assetValues.currentValueToday') }}</span>
          </div>
          <div class="data-card flex flex-col gap-1">
            <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('assetValues.totalDepreciation') }}</span>
            <span class="text-lg font-bold text-red-400">{{ fmtCurrency(totals?.total_depreciation) }}</span>
            <span class="text-[0.65rem]" :class="parseFloat(depreciationPct) > 50 ? 'text-red-400' : 'text-[var(--color-muted)]'">
              {{ depreciationPct ? $t('assetValues.writtenOff', { pct: depreciationPct }) : '—' }}
            </span>
          </div>
        </div>

        <!-- Missing data notice -->
        <div v-if="missingCount > 0" class="flex items-start gap-2.5 bg-amber-950/30 border border-amber-900/60 rounded-xl px-4 py-3 text-sm text-amber-300">
          <MdiAlertCircleOutline class="w-4 h-4 shrink-0 mt-0.5" />
          <span>{{ $t('assetValues.missingDataNotice', { n: missingCount }) }}</span>
        </div>

        <!-- ── Group-by selector ───────────────────────────────────── -->
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-xs text-[var(--color-muted)]">{{ $t('assetValues.groupByLabel') }}</span>
          <button
            v-for="opt in GROUP_OPTIONS"
            :key="opt.value"
            class="btn ghost small"
            :class="groupBy === opt.value ? 'ring-1 ring-[var(--color-primary)] text-[var(--color-primary)]' : ''"
            @click="groupBy = opt.value"
          >{{ opt.label }}</button>
        </div>

        <!-- ── Group table ─────────────────────────────────────────── -->
        <div class="overflow-x-auto rounded-2xl border border-gray-800">
          <table class="w-full text-sm text-left border-collapse">
            <thead class="bg-[var(--color-surface)]">
              <tr class="text-[var(--color-muted)] text-xs border-b border-gray-800">
                <th class="px-4 py-3 font-medium cursor-pointer hover:text-gray-300" @click="setSort('name')">
                  <span class="flex items-center gap-1">
                    {{ GROUP_OPTIONS.find(o => o.value === groupBy)?.label }}
                    <component :is="sortKey === 'name' ? (sortDir === 'asc' ? MdiSortAscending : MdiSortDescending) : MdiSortAscending" class="w-3.5 h-3.5 opacity-40" />
                  </span>
                </th>
                <th class="px-4 py-3 font-medium text-center cursor-pointer hover:text-gray-300" @click="setSort('asset_count')">
                  <span class="flex items-center justify-center gap-1">
                    {{ $t('assetValues.assetsCol') }}
                    <component :is="sortKey === 'asset_count' ? (sortDir === 'asc' ? MdiSortAscending : MdiSortDescending) : MdiSortAscending" class="w-3.5 h-3.5 opacity-40" />
                  </span>
                </th>
                <th class="px-4 py-3 font-medium text-right cursor-pointer hover:text-gray-300" @click="setSort('total_purchase_value')">
                  <span class="flex items-center justify-end gap-1">
                    {{ $t('assetValues.purchaseValueCol') }}
                    <component :is="sortKey === 'total_purchase_value' ? (sortDir === 'asc' ? MdiSortAscending : MdiSortDescending) : MdiSortAscending" class="w-3.5 h-3.5 opacity-40" />
                  </span>
                </th>
                <th class="px-4 py-3 font-medium text-right cursor-pointer hover:text-gray-300" @click="setSort('total_book_value')">
                  <span class="flex items-center justify-end gap-1">
                    {{ $t('assetValues.bookValueCol') }}
                    <component :is="sortKey === 'total_book_value' ? (sortDir === 'asc' ? MdiSortAscending : MdiSortDescending) : MdiSortAscending" class="w-3.5 h-3.5 opacity-40" />
                  </span>
                </th>
                <th class="px-4 py-3 font-medium text-right cursor-pointer hover:text-gray-300" @click="setSort('total_depreciation')">
                  <span class="flex items-center justify-end gap-1">
                    {{ $t('assetValues.depreciatedCol') }}
                    <component :is="sortKey === 'total_depreciation' ? (sortDir === 'asc' ? MdiSortAscending : MdiSortDescending) : MdiSortAscending" class="w-3.5 h-3.5 opacity-40" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-if="sortedGroups.length === 0 && !loading"
                class="text-center"
              >
                <td colspan="5" class="px-4 py-8 text-[var(--color-muted)] text-sm">{{ $t('assetValues.noData') }}</td>
              </tr>
              <tr
                v-for="group in sortedGroups"
                :key="group.id ?? group.name"
                class="border-b border-gray-900 hover:bg-[var(--color-surface-elevated,#1c1c1c)] transition-colors"
              >
                <td class="px-4 py-3 font-medium text-gray-200">
                  {{ group.name }}
                  <span v-if="group.valued_count < group.asset_count" class="ml-2 badge badge-warning badge-pill text-[0.6rem]">
                    {{ $t('assetValues.unvalued', { n: group.asset_count - group.valued_count }) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center text-gray-400">{{ group.asset_count }}</td>
                <td class="px-4 py-3 text-right font-mono text-gray-300">{{ fmtCurrency(group.total_purchase_value) }}</td>
                <td class="px-4 py-3 text-right font-mono text-amber-300">{{ fmtCurrency(group.total_book_value) }}</td>
                <td class="px-4 py-3 text-right">
                  <span class="font-mono text-red-400/80">-{{ fmtCurrency(group.total_depreciation) }}</span>
                  <span class="ml-2 text-xs text-gray-600">{{ fmtPct(group.total_purchase_value, group.total_book_value) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Loading overlay on re-fetch -->
        <div v-if="loading && data" class="text-xs text-center text-[var(--color-muted)] py-2 animate-pulse">
          {{ $t('assetValues.updating') }}
        </div>

      </template>
    </div>
  </div>
</template>
